import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, EMPTY, from, interval, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap } from 'rxjs/operators';

import { APP_CONFIG, ENV_CONFIG, FeatureFlag, ObjectUtils, UserAppSettingsKey } from '@bn/primitives';
import { HomeFeed, UserDetails, UserMarketingDetailsMode } from '@bn/shared/core/api';
import { PosthogService } from '@bn/shared/core/services';
import { BnToastrService } from '@bn/shared/toastr';

import { TypeValueUserAppSettings, UserAppSettings } from '../models/user-app-settings.model';
import { userDetailInterval } from '../stores/helpers';

import { AuthService } from './auth.service';
import { UserDetailsApiService } from './user-details-api.service';

@Injectable({
  providedIn: 'root',
})
export class UserDetailsStateService implements OnDestroy {
  readonly #APP_CONFIG = inject(APP_CONFIG);
  readonly #ENV_CONFIG = inject(ENV_CONFIG);
  readonly #httpClient = inject(HttpClient);
  readonly #authService = inject(AuthService);
  readonly #apiService = inject(UserDetailsApiService);
  readonly #posthog = inject(PosthogService);
  readonly #subscriptions = new Subscription();
  readonly #toastr = inject(BnToastrService);
  readonly #apiUrl!: string;

  private readonly userDetails$ = new BehaviorSubject<UserDetails | null>(null);
  private readonly userAppSettings$: BehaviorSubject<UserAppSettings>;
  private readonly intervals = userDetailInterval(this.#APP_CONFIG.api);
  private readonly userPollInterval = new BehaviorSubject(this.intervals['jwt']);
  private readonly userAppSettingsKey = '🐇-app-settings';

  public constructor() {
    this.#apiUrl = `${this.#ENV_CONFIG.apiUrl}/user`;

    this.userAppSettings$ = new BehaviorSubject(JSON.parse(localStorage.getItem(this.userAppSettingsKey) ?? '{}'));
  }

  ngOnDestroy(): void {
    this.#subscriptions.unsubscribe();
  }

  public initUserDetailsInterval(): void {
    this.#subscriptions.add(
      this.userPollInterval
        .pipe(
          switchMap((time) => interval(time)),
          exhaustMap(() => this.getFreshUserDetails()),
          tap((user) => {
            // change polling pace if KYC criteria is not met
            this.userPollInterval.next(
              user.Roles.includes('User') && (!user.EmailVerified || !user.HasCompleteBillingProfile) ? this.intervals.incompleteKyc : this.intervals.jwt,
            );
          }),
          // continue if user object has changed. Is anyone dependant on refresh interval?
          distinctUntilChanged((a, b) => ObjectUtils.isDeepEqual(a, b)),
        )
        .subscribe({
          next: (userDetails) => this.userDetails$.next(userDetails),
          error: () => this.#toastr.danger(`Please refresh your page.`, `Couldn't fetch fresh user data`),
        }),
    );
  }

  public getUserHomeFeed(): Observable<HomeFeed> {
    return this.#httpClient.get<HomeFeed>(`${this.#apiUrl}/homefeed`).pipe(
      switchMap((homeFeed) => {
        const cleanedBlogPostsHomeFeed = homeFeed.BlogPosts.map((blogPost) => {
          const element = document.createElement('div');
          element.innerHTML = blogPost.Summary;
          const firstParagraph = element.getElementsByTagName('p')[0] ?? '';

          return {
            ...blogPost,
            Summary: firstParagraph.innerText || firstParagraph.textContent || blogPost.Summary,
          };
        });

        return of({ ...homeFeed, BlogPosts: cleanedBlogPostsHomeFeed });
      }),
    );
  }

  public getUserDetails(): Observable<UserDetails | null> {
    return this.userDetails$.asObservable().pipe(switchMap((userDetails) => (userDetails ? of(userDetails) : EMPTY)));
  }

  public getFreshUserDetails(): Observable<UserDetails> {
    if (!this.#authService.isLoggedIn()) return EMPTY;

    return this.#apiService.getFreshUserDetails().pipe(
      switchMap((userDetails) => {
        return this.getMarketingDetails().pipe(
          map((marketingDetails) => {
            this.userDetails$.next(userDetails);
            this.#posthog.setUser(userDetails);
            this.#posthog.identifyUser(marketingDetails);

            return userDetails;
          }),
          catchError((error) => {
            console.error(`UserMarketingDetails fetch failed with: ${error.message}`, error);
            return of(userDetails);
          }),
        );
      }),
    );
  }

  public getFeatureFlags(): Observable<UserDetails['FeatureFlags']> {
    return this.userDetails$.pipe(map((userDetails) => userDetails?.FeatureFlags ?? []));
  }

  public getMarketingDetails(): Observable<UserMarketingDetailsMode> {
    if (!this.#authService.isLoggedIn()) return EMPTY;

    return this.#apiService.getMarketingDetails();
  }

  public updateUserDetails(userDetails: Partial<UserDetails>, successMessage?: string, updateMail = true): Observable<UserDetails | null> {
    const message = successMessage ? successMessage : 'Account details successfully updated.';

    const userObject = {
      ...(this.userDetails$.getValue() ?? {}),
      ...userDetails,
    };

    if (!updateMail && userObject.Email) delete userObject.Email;

    return this.#apiService.updateUserDetails(userObject).pipe(
      switchMap(() => from(this.getFreshUserDetails())),
      tap(() => this.#toastr.success(message, 'Saved')),
    );
  }

  public resendConfirmationEmail(): Observable<null> {
    return this.#apiService.resendConfirmationEmail().pipe(tap(() => this.#toastr.success('User confirmation email was resent.', 'Success')));
  }

  public getUserAppSettings(): Observable<UserAppSettings> {
    return this.userAppSettings$.asObservable();
  }

  public appendUserAppSettings(key: UserAppSettingsKey, value: TypeValueUserAppSettings): void {
    const newSettings: UserAppSettings = {
      ...this.userAppSettings$.getValue(),
      [key]: value,
    };

    this.userAppSettings$.next(newSettings);
    localStorage.setItem(this.userAppSettingsKey, JSON.stringify(newSettings)); // permanent
  }

  public disableTwoFactorAuthentication(Password: string): Observable<void> {
    return this.#apiService.disableTwoFactorAuthentication(Password).pipe(
      switchMap(() => this.#authService.refreshToken()),
      switchMap(() => this.getFreshUserDetails()),
      tap(() => this.#toastr.success('Two-factor authentication sucessfully disabled.', 'Success')),
      map(() => void 0),
    );
  }

  public checkFeatureFlag(featureFlag: FeatureFlag): Observable<boolean> {
    return this.getUserDetails().pipe(
      map((userDetails: UserDetails | null) => {
        if (!userDetails) return false;

        const features = userDetails.FeatureFlags || [];
        return features.includes(featureFlag);
      }),
    );
  }

  public extendFreeTrial(): Observable<null> {
    return this.#apiService.extendFreeTrial().pipe(tap(() => this.getFreshUserDetails().subscribe()));
  }

  public extendFreeTrialAllUsers(): Observable<null> {
    return this.#apiService.extendFreeTrialAllUsers().pipe(tap(() => this.getFreshUserDetails().subscribe()));
  }
}
